import { defineStore, storeToRefs } from 'pinia';
import { uniqueId } from 'lodash';
import type { Cartv2PackageDisplayModel, Cartv2PackageItemDisplayModel } from '@shared/models/carts/cartPackages';
import type { PackageDisplayModel } from '@shared/models/packages/packages';
import type { SubjectImageDisplayModel } from '@shared/models/subjects/images';
import type { SubjectFieldDisplayModel } from '@shared/models/subjects/subjectFields';
import type { SubjectDisplayModel } from '@shared/models/subjects/subjects';
import { getAutoFillImageNodes } from '@shared/services/packageNodes.service';
import { allowCrop } from '@shared/services/cropper.service';
import type { identifier } from '@core/models/base';
import type { ServiceResult } from '@core/models/actionResponse';
import { usePackageAddonService } from '@apps/job-editor/services/packageAddon.service';
import type { GreenScreenBackgroundDisplayModel } from '@shared/models/packages/greenScreenBackgrounds';
import { useCartStore } from '@apps/customer/stores/cart.store';
import { useCustomerStore } from '@apps/customer/stores/customer.store';
import {
  SINGLE_DIGITAL_DOWNLOAD,
  SINGLE_DIGITAL_DOWNLOAD_RSCODE,
  WHOLE_DIGITAL_DOWNLOAD,
  WHOLE_DIGITAL_DOWNLOAD_RSCODE,
} from '@shared/models/products/productLookup';
import { fetchCart } from '@apps/cart/services/cart.service';
import { areImageNodesComplete, isItemComplete, areTextNodesComplete } from '../services/cartPackageNode.service';
import { getCartPackageFromPackageAndImage, getCartPackageFromServer } from '../services/cartPackageModels.service';
import { createCartPackage, patchCartPackageDetailsToServer } from '../services/cartPackage.service';
import type { ICartPackageStoreState, ICartPackageStoreItem } from './cartPackage.cache';
import { useCurrencyArithmetic } from '@shared/composables/useCurrencyArithmetic';

const { validatePackage, hasStandardPackage, isAddon } = usePackageAddonService();
const { multiply, subtract, add } = useCurrencyArithmetic();
export const useCartPackageStore = defineStore('cartPackage', {
  state: (): ICartPackageStoreState => ({
    items: [],
    code: undefined,
    saving: false,
  }),

  getters: {
    total: (state): number => {
      const cartStore = useCartStore();
      const { total } = storeToRefs(cartStore);
      const itemPrices = state.items?.map((i) => multiply(i.jobPackage.retailCost, i.cartPackage.quantity)) ?? [0];
      return add(total.value, ...itemPrices);
    },
    hasStandardPackage: (state) => hasStandardPackage(state.items?.map((i) => i.jobPackage)),

    addonPackages: (state) => (state.items.length ? state.items.filter((i) => isAddon(i.jobPackage)) : []),
    getItemByPackage(state) {
      const fn = (pkg: PackageDisplayModel) => state.items.find((item) => item.jobPackage.id === pkg.id);
      return fn;
    },

    editableItems(state): Map<Cartv2PackageDisplayModel, Cartv2PackageItemDisplayModel[]> {
      const customerStore = useCustomerStore();
      return new Map<Cartv2PackageDisplayModel, Cartv2PackageItemDisplayModel[]>(
        state.items.map((item) => [
          item.cartPackage,
          item.cartPackage?.cartPackageItems.filter(
            (i) =>
              (!customerStore.isPrePayRspo && i.imageNodes.length) ||
              i.textNodes.filter((n) => {
                if (customerStore.hasImportedData) return !n.autoFilled;
                return n;
              }).length ||
              i.productCode === WHOLE_DIGITAL_DOWNLOAD ||
              i.productCode === SINGLE_DIGITAL_DOWNLOAD ||
              i.productCode === SINGLE_DIGITAL_DOWNLOAD_RSCODE ||
              i.productCode === WHOLE_DIGITAL_DOWNLOAD_RSCODE,
          ) ?? [],
        ]),
      );
    },

    /** 
     Check if the cart package store has any editable items or the job has Green Screens.
     Editable items are items that have image nodes (when hosted) or text nodes.
     Editable items are digitale items
     Editable items are available to customers with green screen backgrounds.
    */
    hasEditableItems(state) {
      const customerStore = useCustomerStore();
      const editableItems = this.editableItems as Map<Cartv2PackageDisplayModel, Cartv2PackageItemDisplayModel[]>;
      const values = Array.from(editableItems?.values());
      return (!!values.length && values.some((v) => v.length)) || customerStore.hasGreenScreens;
    },

    getDefaultImageNode(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        return item?.jobPackage ? getAutoFillImageNodes(item.jobPackage)[0] : null;
      };

      return fn;
    },

    packageHasCroppableItems(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        if (item?.jobPackage) {
          return item.jobPackage.items.some((i) => allowCrop(item.jobPackage as PackageDisplayModel, i));
        }

        return false;
      };

      return fn;
    },

    hasCroppableItems: (state) => {
      for (const item of state.items) {
        if (item.jobPackage.items.some((i) => allowCrop(item.jobPackage, i))) {
          return true;
        }
      }

      return false;
    },

    packageHasAutoFillableNodes(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        return item?.jobPackage && getAutoFillImageNodes(item.jobPackage).length;
      };

      return fn;
    },

    hasAutoFillableNodes: (state) =>
      state.items.filter((i) => i.jobPackage).some((i) => getAutoFillImageNodes(i.jobPackage!).length),

    allPackageItemsComplete(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        return item?.cartPackage?.cartPackageItems.every((i) => isItemComplete(i));
      };

      return fn;
    },

    allItemsComplete: (state) => state.items.flatMap((i) => i.cartPackage.cartPackageItems).every(isItemComplete),

    allTextItemsComplete: (state) =>
      state.items.flatMap((i) => i.cartPackage.cartPackageItems).every(areTextNodesComplete),

    allImageItemsComplete: (state) =>
      state.items.flatMap((i) => i.cartPackage.cartPackageItems).every(areImageNodesComplete),

    packageImageNodes(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        return item?.jobPackage?.items.map((i) => i.imageNodes).flat() || [];
      };

      return fn;
    },

    allImageNodes: (state) => state.items.flatMap((i) => i.jobPackage.items).flatMap((i) => i.imageNodes),

    packageGreenScreenSelected(state) {
      const fn = (pkg: PackageDisplayModel) => {
        const item = this.getItemByPackage(pkg);
        return !!item?.cartPackage?.greenScreen?.id;
      };

      return fn;
    },

    greenScreensSelected: (state) => state.items.every((i) => i.cartPackage?.greenScreen?.id),
  },
  actions: {
    initializeFromImageAndPackage(
      pkg: PackageDisplayModel,
      subject: SubjectDisplayModel,
      image?: SubjectImageDisplayModel,
    ) {
      if (!this.items) {
        this.items = [];
      }

      const item: ICartPackageStoreItem = {
        id: uniqueId(),
        subject: subject,
        jobPackage: pkg,
        cartPackage: getCartPackageFromPackageAndImage(pkg, subject, image),
      };

      this.items.push(item);
    },

    initializeFromServer(
      cartPackage: Cartv2PackageDisplayModel,
      jobPackage: PackageDisplayModel,
      subject: SubjectDisplayModel,
      hasImportedData: boolean,
    ) {
      if (!this.items) {
        this.items = [];
      }

      const item: ICartPackageStoreItem = {
        id: uniqueId(),
        subject: subject,
        jobPackage: jobPackage,
        cartPackage: getCartPackageFromServer(cartPackage, subject, hasImportedData),
      };

      this.items.push(item);
    },

    checkPriceAddonStatus() {
      if (this.items?.length) {
        this.items = this.items.filter((i) =>
          validatePackage(i.cartPackage, multiply(i.jobPackage.retailCost, i.cartPackage.quantity), this.total),
        );
      }
    },

    updateCartPackageQuantity(pkg: Cartv2PackageDisplayModel, quantity: number) {
      if (pkg) {
        const item = this.items?.find((i) => i.cartPackage.id === pkg.id);
        if (item && quantity === 0) {
          this.removeItem(item);
        } else if (item) {
          item.cartPackage.quantity = quantity;
          this.checkPriceAddonStatus();
        }
      }
    },

    removeItem(item?: ICartPackageStoreItem) {
      if (item) {
        this.items = this.items?.filter((i) => i !== item);

        if (!this.hasStandardPackage) {
          this.clear();
        } else this.checkPriceAddonStatus();
      }
    },

    removeItemByJobPackage(pkg: PackageDisplayModel) {
      this.removeItem(this.getItemByPackage(pkg));
    },

    removeItemByItemId(storeItemId: string) {
      this.removeItem(this.items.find((i) => i.id === storeItemId));
    },

    removeItemByCartPackage(pkg: Cartv2PackageDisplayModel) {
      this.removeItem(this.items?.find((i) => i.cartPackage.id === pkg.id));
    },

    useCode(code: string) {
      this.code = code;
    },

    setGreenScreen(storeItem: ICartPackageStoreItem, greenScreen: GreenScreenBackgroundDisplayModel) {
      storeItem.cartPackage.greenScreen = greenScreen;
    },

    updateCartPackage(storeItemId: string, update: Cartv2PackageDisplayModel) {
      const item = this.items.find((i) => i.id === storeItemId);
      if (item) {
        item.cartPackage = update;
      }
    },

    updateItems(items: ICartPackageStoreItem[]) {
      this.items = items;
    },

    clear() {
      this.items = [];
    },

    updateDataFields(storeItemId: string, fields: SubjectFieldDisplayModel[]) {
      const item = this.items.find((i) => i.id === storeItemId);
      if (item) {
        item.dataFields = fields;
      }
    },

    updateCartPackageItem(storeItemId: string, itemId: identifier, item: Cartv2PackageItemDisplayModel) {
      const storeItem = this.items.find((i) => i.id === storeItemId);
      if (storeItem) {
        storeItem.cartPackage?.cartPackageItems.splice(
          storeItem.cartPackage.cartPackageItems.findIndex((i) => i.id === itemId),
          1,
          item,
        );
      }
    },

    async postCartPackages(jobId: identifier): Promise<ServiceResult<Cartv2PackageDisplayModel>[]> {
      const cartStore = useCartStore();
      await fetchCart(jobId, cartStore.persistedOrderMethod!);
      const results = await Promise.all(
        this.items.map(async (i) => await this.postCartPackage(jobId, i.cartPackage.id)),
      );

      this.clear();
      await cartStore.loadDetails(jobId);

      return results;
    },

    async postCartPackage(
      jobId: identifier,
      cartPackageId: identifier,
    ): Promise<ServiceResult<Cartv2PackageDisplayModel>> {
      const item = this.items?.find((i) => i.cartPackage?.id === cartPackageId);
      if (!item || !item.cartPackage || !item.subject) return { errors: ['An error occurred'] };

      this.saving = true;

      const cartStore = useCartStore();
      if (!cartStore.persistedOrderMethod) {
        console.warn('Order method not set');
      }

      const result = await createCartPackage(
        jobId,
        item.cartPackage.packageId,
        item.subject.id,
        cartStore.persistedOrderMethod!,
        item.cartPackage,
        item.dataFields,
      );
      this.saving = false;
      return result;
    },

    async putCartPackage(jobId: identifier, cartPackageId: identifier): Promise<ServiceResult<boolean>> {
      const item = this.items?.find((i) => i.cartPackage?.id === cartPackageId);

      if (item && item.cartPackage) {
        try {
          this.saving = true;
          await patchCartPackageDetailsToServer(jobId, item.cartPackage);
        } catch (e: any) {
          return { data: false, errors: [e.message] };
        } finally {
          this.saving = false;
        }
      }
      return { data: true };
    },
  },
});
